import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, Timer } from "lucide-react";
import { apiFetch } from "@/api";
import PageBlobs from "@/components/PageBlobs";

/* Exam Mode paper — all questions on one scrolling page, countdown timer,
   no instant feedback. Submitting grades objectives instantly and sends
   theory answers to the existing batch AI evaluation. */

const TYPE_LABELS = {
  MCQ: "MCQ",
  STATEMENT_NUMERICAL: "Numerical",
  STATEMENT_THEORY: "Theory",
  DIAGRAM_BASED: "Diagram",
};

const num = (v) => {
  const n = parseFloat(String(v).replace(/[₹,%\s]/g, "").replace(/:1$/, ""));
  return Number.isFinite(n) ? n : null;
};

const valuesMatch = (given, correct) => {
  const g = num(given);
  const c = num(correct);
  if (g !== null && c !== null) {
    if (c === 0) return g === 0;
    return Math.abs(g - c) / Math.abs(c) < 0.01;
  }
  return String(given).trim().toLowerCase() === String(correct).trim().toLowerCase();
};

const fmtClock = (sec) =>
  `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;

const ExamPaper = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { questions: raw, exam, board, klass } = location.state || {};

  const questions = useMemo(
    () =>
      (raw || []).map((q) => ({
        qtype: q.question_type || (q.options?.length ? "MCQ" : "STATEMENT_THEORY"),
        q: q.question_text || q.question,
        answer: q.final_answer || q.answer,
        options: q.options?.length ? q.options : null,
        correctOption: typeof q.correct_option === "number" ? q.correct_option : null,
        marks: q.marks || 1,
      })),
    [raw]
  );

  const [selections, setSelections] = useState({});
  const [texts, setTexts] = useState({});
  const [remaining, setRemaining] = useState(exam?.timeLimitSec ?? 1800);
  const [submitting, setSubmitting] = useState(false);
  const startedAt = useRef(Date.now());
  const submitted = useRef(false);

  // countdown + auto-submit at zero
  useEffect(() => {
    if (!exam) return;
    const t = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(t);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [exam]);

  const answeredCount = questions.reduce(
    (n, _q, i) => n + (selections[i] !== undefined || (texts[i] || "").trim() ? 1 : 0),
    0
  );

  const submit = async () => {
    if (submitted.current || submitting) return;
    submitted.current = true;
    setSubmitting(true);
    const timeTakenSec = Math.round((Date.now() - startedAt.current) / 1000);

    const review = questions.map((q, i) => {
      let student_answer = null;
      let is_correct = null;
      if (q.qtype === "MCQ" && q.options) {
        const sel = selections[i];
        if (sel !== undefined) {
          student_answer = q.options[sel];
          is_correct =
            q.correctOption !== null ? sel === q.correctOption : q.options[sel] === q.answer;
        }
      } else {
        const txt = (texts[i] || "").trim();
        if (txt) {
          student_answer = txt;
          if (q.qtype === "STATEMENT_NUMERICAL" || q.qtype === "DIAGRAM_BASED") {
            is_correct = valuesMatch(txt, q.answer);
          }
          // theory: is_correct stays null — AI evaluates next
        }
      }
      return {
        question: q.q,
        question_type: q.qtype,
        options: q.options || undefined,
        answer: q.answer,
        student_answer,
        is_correct,
        marks: q.marks,
      };
    });

    // Batch AI evaluation for attempted theory answers (same endpoint as practice)
    const theoryIdx = review
      .map((q, i) => (q.question_type === "STATEMENT_THEORY" && q.student_answer ? i : -1))
      .filter((i) => i >= 0);
    if (theoryIdx.length) {
      try {
        const d = await apiFetch("/evaluate-theory", {
          method: "POST",
          body: {
            board: board || "CBSE",
            klass: String(klass || "10"),
            subject: exam.subject,
            chapter: exam.scope || exam.label,
            difficulty: "Medium",
            items: theoryIdx.map((i) => ({
              question: review[i].question,
              student_answer: review[i].student_answer,
              model_answer: review[i].answer,
            })),
          },
        });
        (d.evaluations || []).forEach((ev) => {
          const q = review[theoryIdx[ev.index]];
          if (q) {
            q.evaluation = ev.verdict;
            q.earned = Math.round(ev.marks * q.marks * 2) / 2; // fraction × paper marks
            q.feedback = ev.feedback;
          }
        });
      } catch {
        /* theory left unevaluated — scored as 0, still reviewable */
      }
    }

    navigate("/practice-complete", {
      state: {
        subject: exam.subject,
        chapter: exam.scope || exam.label,
        board,
        klass,
        difficulty: "Exam",
        total: questions.length,
        review,
        exam: { ...exam, timeTakenSec },
      },
    });
  };

  // auto-submit when the clock hits zero
  useEffect(() => {
    if (remaining === 0 && !submitted.current) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining]);

  if (!exam || !questions.length) return <Navigate to="/exam-mode" replace />;

  const lowTime = remaining <= 120;

  return (
    <div
      data-testid="exam-paper"
      className="relative min-h-screen bg-[#F8FAF7] font-body text-[#17201A]"
    >
      <PageBlobs />
      {/* Sticky exam bar */}
      <header className="sticky top-0 z-40 border-b border-[#E3ECE6] bg-[#F8FAF7]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-3.5">
          <div className="min-w-0">
            <p data-testid="exam-label" className="truncate font-display text-sm font-extrabold">
              {exam.label} · {exam.subject}
            </p>
            <p className="text-[11px] font-bold text-[#647067]">
              {exam.count} questions · {exam.totalMarks} marks
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span
              data-testid="exam-timer"
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-extrabold tabular-nums ${
                lowTime ? "animate-pulse bg-red-50 text-red-600" : "bg-[#16A34A]/10 text-[#14532D]"
              }`}
            >
              <Timer className="h-4 w-4" />
              {fmtClock(remaining)}
            </span>
            <button
              data-testid="exam-submit-top-btn"
              onClick={submit}
              disabled={submitting}
              className="rounded-full bg-[#16A34A] px-5 py-2 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#15803D] disabled:opacity-50"
            >
              Submit
            </button>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-3xl px-6 pb-28 pt-8">
        {lowTime && (
          <p
            data-testid="exam-low-time-warning"
            className="mb-5 inline-flex items-center gap-2 rounded-full bg-red-50 px-4 py-2 text-xs font-bold text-red-600"
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            Less than 2 minutes left — the paper submits automatically.
          </p>
        )}

        <div className="space-y-5">
          {questions.map((q, i) => (
            <div
              key={i}
              data-testid={`exam-q-${i}`}
              className="rounded-[1.5rem] border border-[#E3ECE6] bg-white p-6 sm:p-8"
            >
              <div className="flex items-start justify-between gap-3">
                <h2 className="font-display text-lg font-bold leading-snug sm:text-xl">
                  <span className="mr-2 text-[#16A34A]">Q{i + 1}.</span>
                  {q.q}
                </h2>
                <span className="shrink-0 rounded-full bg-[#17201A]/8 px-3 py-1 text-[10px] font-bold text-[#647067]">
                  {TYPE_LABELS[q.qtype] || "Theory"} · {q.marks}m
                </span>
              </div>

              {q.options ? (
                <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
                  {q.options.map((opt, oi) => {
                    const sel = selections[i] === oi;
                    return (
                      <button
                        key={oi}
                        data-testid={`exam-option-${i}-${oi}`}
                        onClick={() => setSelections((p) => ({ ...p, [i]: oi }))}
                        disabled={submitting}
                        className={`rounded-xl border-2 px-4 py-3 text-left text-sm font-semibold transition-colors duration-150 ${
                          sel
                            ? "border-[#16A34A] bg-[#16A34A]/8 text-[#17201A]"
                            : "border-[#17201A]/10 bg-[#F8FAF7] hover:border-[#16A34A]/50"
                        }`}
                      >
                        <span className="mr-2 font-display font-bold text-[#16A34A]">
                          {String.fromCharCode(65 + oi)}.
                        </span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              ) : q.qtype === "STATEMENT_THEORY" ? (
                <textarea
                  data-testid={`exam-input-${i}`}
                  rows={3}
                  value={texts[i] || ""}
                  onChange={(e) => setTexts((p) => ({ ...p, [i]: e.target.value }))}
                  disabled={submitting}
                  placeholder="Write your answer…"
                  className="mt-5 w-full resize-none rounded-xl border-2 border-[#17201A]/10 bg-[#F8FAF7] px-4 py-2.5 text-sm font-semibold outline-none transition-colors duration-200 focus:border-[#16A34A]"
                />
              ) : (
                <input
                  data-testid={`exam-input-${i}`}
                  value={texts[i] || ""}
                  onChange={(e) => setTexts((p) => ({ ...p, [i]: e.target.value }))}
                  disabled={submitting}
                  placeholder="Your answer"
                  className="mt-5 w-full max-w-xs rounded-xl border-2 border-[#17201A]/10 bg-[#F8FAF7] px-4 py-2.5 text-sm font-semibold outline-none transition-colors duration-200 focus:border-[#16A34A]"
                />
              )}
            </div>
          ))}
        </div>

        {/* Submit bar */}
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#E3ECE6] bg-white/95 backdrop-blur-md">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-6 py-4">
            <p data-testid="exam-answered-count" className="text-xs font-bold text-[#647067]">
              {answeredCount} of {questions.length} answered
            </p>
            <button
              data-testid="exam-submit-btn"
              data-magnetic="true"
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-full bg-[#16A34A] px-8 py-3.5 text-sm font-bold text-white shadow-sm transition-[background-color,opacity] duration-200 hover:bg-[#15803D] disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Evaluating…
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Submit Paper
                </>
              )}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExamPaper;
